import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createSelectors } from "./createSelectorsFunction";
import type { UnitTestResult } from "~/services/unitTest.services";

type UnitTestState = {
  unitTestResult: UnitTestResult | null;
  isGenerating: boolean;
  testPhase: "generation" | "execution" | null;
  isModalVisible: boolean;
  error: string | null;
  errorType: "generation" | "execution" | null;
  progress: number;
  progressMessage: string;
  /** Path of the contract file the current generation/execution targets. */
  targetFile: string | null;
  actions: {
    setUnitTestResult: (result: UnitTestResult | null) => void;
    setIsGenerating: (isGenerating: boolean) => void;
    setError: (error: string | null, type?: "generation" | "execution") => void;
    clearUnitTestResult: () => void;
    setModalVisibility: (isVisible: boolean) => void;
    setInitialUnitTestState: () => void;
    setGeneratingStatus: () => void;
    setGenerationError: (error: string, type?: "generation" | "execution") => void;
    setProgress: (progress: number, message?: string) => void;
    setTestPhase: (phase: "generation" | "execution" | null) => void;
    setTargetFile: (file: string | null) => void;
    clearProgress: () => void;
  };
};

const initialState: Omit<UnitTestState, "actions"> = {
  unitTestResult: null,
  isGenerating: false,
  testPhase: null,
  isModalVisible: false,
  error: null,
  errorType: null,
  progress: 0,
  progressMessage: "Starting unit test generation",
  targetFile: null,
};

const unitTestStore = immer<UnitTestState>((set: any) => ({
  ...initialState,
  actions: {
    setUnitTestResult: (result: UnitTestResult | null) => {
      // Keep `targetFile` intact: once results arrive it represents the file
      // those results belong to, until the user explicitly clears them.
      set(
        {
          unitTestResult: result,
          error: null,
          errorType: null,
          isGenerating: false,
          testPhase: null,
          progress: 0,
          progressMessage: "",
        },
        false,
        "Set Unit Test Result",
      );
    },
    setIsGenerating: (isGenerating: boolean) => {
      set(
        {
          isGenerating,
          testPhase: isGenerating ? "generation" : null,
          // Apri il modale quando si inizia la generazione, ma permetti la chiusura
          isModalVisible: isGenerating
            ? true
            : (state: any) => state.isModalVisible,
          error: null,
          errorType: null,
          progress: isGenerating ? 0 : (state: any) => state.progress,
          progressMessage: isGenerating
            ? ""
            : (state: any) => state.progressMessage,
        },
        false,
        "Set Is Generating",
      );
    },
    setError: (error: string | null, type?: "generation" | "execution") => {
      set(
        {
          error,
          errorType: type || "generation",
          isGenerating: false,
          testPhase: null,
          isModalVisible: true,
          progress: 0,
          progressMessage: "",
        },
        false,
        "Set Error",
      );
    },
    clearUnitTestResult: () => {
      set(
        {
          unitTestResult: null,
          isModalVisible: false,
          isGenerating: false,
          testPhase: null,
          error: null,
          errorType: null,
          progress: 0,
          progressMessage: "",
          targetFile: null,
        },
        false,
        "Clear Unit Test Result",
      );
    },
    setModalVisibility: (isVisible: boolean) => {
      // Permetti sempre di chiudere il modale, anche durante la generazione
      set({ isModalVisible: isVisible }, false, "Set Modal Visibility");
    },
    setInitialUnitTestState: () => {
      set(
        {
          unitTestResult: null,
          isModalVisible: true,
          isGenerating: false,
          testPhase: null,
          error: null,
          errorType: null,
          progress: 0,
          progressMessage: "",
        },
        false,
        "Set Initial Unit Test State",
      );
    },
    setGeneratingStatus: () => {
      set(
        {
          isGenerating: true,
          testPhase: "generation",
          // Non forzare l'apertura del modale se l'utente l'ha già chiuso
          isModalVisible: true,
          error: null,
          errorType: null,
          progress: 0,
          progressMessage: "",
        },
        false,
        "Set Generating Status",
      );
    },
    setGenerationError: (error: string, type?: "generation" | "execution") => {
      set(
        {
          error,
          errorType: type || "generation",
          isGenerating: false,
          testPhase: null,
          isModalVisible: true,
          progress: 0,
          progressMessage: "",
        },
        false,
        "Set Generation Error",
      );
    },
    setProgress: (progress: number, message?: string) => {
      set(
        {
          progress,
          progressMessage: message || "",
        },
        false,
        "Set Progress",
      );
    },
    setTestPhase: (phase: "generation" | "execution" | null) => {
      set(
        {
          testPhase: phase,
        },
        false,
        "Set Test Phase",
      );
    },
    setTargetFile: (file: string | null) => {
      set(
        {
          targetFile: file,
        },
        false,
        "Set Target File",
      );
    },
    clearProgress: () => {
      set(
        {
          progress: 0,
          progressMessage: "",
        },
        false,
        "Clear Progress",
      );
    },
  },
}));

const useUnitTestStoreBase = create(
  subscribeWithSelector(devtools(unitTestStore, { name: "UnitTestStore" })),
);
const useUnitTestStore = createSelectors(useUnitTestStoreBase);

export const useUnitTestStoreActions = () =>
  useUnitTestStore((state: any) => state.actions);

export default useUnitTestStore;
